import i18n from "../translations/i18n";
import type { Translation, ItemLang } from "../api/menu";

/**
 * Извлича локализирано съобщение за грешка от Axios грешка.
 * Backend-ът връща { detail: { en: "...", bg: "..." } } при 4xx грешки.
 * Взима съобщението на текущия език, с fallback към "bg", след това към fallbackMessage.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { response?: { data?: { detail?: unknown } } };
    const detail = axiosError.response?.data?.detail;

    if (detail && typeof detail === "object" && !Array.isArray(detail)) {
      // Backend е върнал обект { en: "...", bg: "..." }
      const detailMap = detail as Record<string, string>;
      const currentLang = i18n.language?.split("-")[0] ?? "bg";
      return detailMap[currentLang] ?? detailMap["bg"] ?? fallbackMessage;
    }

    if (typeof detail === "string" && detail.length > 0) {
      return detail;
    }
  }

  if (typeof error === "string" && error.length > 0) {
    return error;
  }

  return fallbackMessage;
};

/**
 * Извлича превод на конкретен език от масив с преводи
 */
export const getTranslation = (
  translations: Translation[] | undefined,
  languageCode: string = "bg",
  fallback: string = "",
): string => {
  if (!translations || translations.length === 0) {
    return fallback;
  }
  return (
    translations.find((t) => t.languageCode === languageCode)?.name || fallback
  );
};

/**
 * Извлича само името на артикул на конкретен език
 */
export const getItemName = (
  itemLangs: ItemLang[] | undefined,
  languageCode: string = "bg",
  fallback: string = "",
): string => {
  if (!itemLangs || itemLangs.length === 0) {
    return fallback;
  }
  return (
    itemLangs.find((lang) => lang.languageCode === languageCode)?.name ||
    fallback
  );
};

/**
 * Извлича описанието на артикул на конкретен език
 */
export const getItemDescription = (
  itemLangs: ItemLang[] | undefined,
  languageCode: string = "bg",
  fallback: string | null = null,
): string | null => {
  if (!itemLangs || itemLangs.length === 0) {
    return fallback;
  }
  const translation = itemLangs.find(
    (lang) => lang.languageCode === languageCode,
  );
  return translation?.description || fallback;
};

/**
 * Извлича unitAndSize на артикул на конкретен език
 */
export const getItemUnitAndSize = (
  itemLangs: ItemLang[] | undefined,
  languageCode: string = "bg",
  fallback: string | null = null,
): string | null => {
  if (!itemLangs || itemLangs.length === 0) {
    return fallback;
  }
  const translation = itemLangs.find(
    (lang) => lang.languageCode === languageCode,
  );
  return translation?.unitAndSize || fallback;
};
